"use client";

import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  certificateAssets,
  getPdfPreviewUrl,
  getPdfDownloadUrl,
} from "@/lib/pdf-assets";
import { PdfPreview } from "@/components/pdf-preview";
import { LiquidGlassCard } from "@/components/ui/liquid-glass-card";
import { usePortfolio } from "@/components/portfolio-provider";
import {
  GraduationCap,
  Award,
  Code2,
  TrendingUp,
  Globe,
  MapPin,
  Calendar,
  Eye,
  Download,
  X,
  Sparkles,
} from "lucide-react";

const stats = [
  { label: "Years Experience", value: "4+", icon: TrendingUp },
  { label: "Stack Expertise", value: "Next.js + Nest.js", icon: Code2 },
  { label: "Query Optimization", value: "35%", icon: TrendingUp },
  { label: "Work Availability", value: "Remote & Onsite", icon: Globe },
];

const training = certificateAssets;

const education = [
  {
    degree: "B.Sc. in Computer Science & Engineering",
    institute: "Daffodil International University",
    period: "2018 – 2024",
    location: "Ashulia, Dhaka",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

function renderSectionHeading(heading?: string) {
  if (!heading) {
    return (
      <>
        Engineering & <span className="accent-gradient-text">Background</span>
      </>
    );
  }

  const words = heading.trim().split(/\s+/);
  if (words.length <= 1) {
    return <span className="accent-gradient-text">{heading}</span>;
  }

  const prefix = words.slice(0, -1).join(" ");
  const lastWord = words[words.length - 1];

  return (
    <>
      {prefix} <span className="accent-gradient-text">{lastWord}</span>
    </>
  );
}

export default function About() {
  const { data } = usePortfolio();
  const aboutData = data?.about;
  const statsData = data?.stats && data.stats.length > 0 ? data.stats : null;
  const educationData =
    data?.education && data.education.length > 0 ? data.education : null;
  const certificateData =
    data?.certificates && data.certificates.length > 0
      ? data.certificates
      : null;

  const displayStats = statsData
    ? statsData.map((s, idx) => ({
        label: s.label,
        value: s.value,
        icon: [TrendingUp, Code2, TrendingUp, Globe][idx % 4] || TrendingUp,
      }))
    : stats;

  const displayEducation = educationData || education;

  const displayCertificates = certificateData
    ? certificateData.map((c) => ({
        id: c.id,
        title: c.title,
        provider: c.provider,
        year: c.year,
        downloadPath: c.pdfUrl,
        downloadName: c.downloadName,
      }))
    : training;

  const [activeCertificateId, setActiveCertificateId] = useState<string | null>(
    null,
  );
  const [isMounted, setIsMounted] = useState(false);

  const activeCertificate = displayCertificates.find(
    (item) => item.id === activeCertificateId,
  );

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    const prefetchLinks = displayCertificates.map((item) => {
      const link = document.createElement("link");
      link.rel = "prefetch";
      link.href = getPdfPreviewUrl(item.downloadPath);
      link.as = "fetch";
      document.head.append(link);
      return link;
    });

    return () => {
      prefetchLinks.forEach((link) => link.remove());
    };
  }, [displayCertificates]);

  useEffect(() => {
    if (!activeCertificateId) return;

    const onEscPress = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setActiveCertificateId(null);
      }
    };

    window.addEventListener("keydown", onEscPress);

    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    const lenis = (window as any).__lenis;
    if (lenis?.stop) lenis.stop();

    return () => {
      window.removeEventListener("keydown", onEscPress);

      if (prevBodyOverflow && prevBodyOverflow !== "hidden") {
        document.body.style.overflow = prevBodyOverflow;
      } else {
        document.body.style.removeProperty("overflow");
      }

      if (prevHtmlOverflow && prevHtmlOverflow !== "hidden") {
        document.documentElement.style.overflow = prevHtmlOverflow;
      } else {
        document.documentElement.style.removeProperty("overflow");
      }

      const activeLenis = (window as any).__lenis;
      if (activeLenis?.start) {
        activeLenis.start();
      }
    };
  }, [activeCertificateId]);

  return (
    <section
      id="about"
      className="section-shell relative overflow-x-clip py-10 sm:py-14 md:py-18 lg:py-20"
    >
      {/* Background Liquid Ambient Light */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -left-20 top-1/3 h-80 w-80 rounded-full bg-[radial-gradient(circle,rgba(201,168,76,0.14)_0%,transparent_70%)]" />
        <div className="absolute right-0 top-2/3 h-72 w-72 rounded-full bg-[radial-gradient(circle,rgba(201,168,76,0.1)_0%,transparent_70%)]" />
      </div>

      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-10"
        >
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-4 py-1.5 backdrop-blur-xl shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <span className="text-xs font-semibold uppercase tracking-wider text-primary">
              {aboutData?.badge || "About Me"}
            </span>
          </div>
          <h2 className="section-title mb-4 font-bold">
            {renderSectionHeading(aboutData?.heading)}
          </h2>
          <p className="section-subtitle mx-auto max-w-2xl text-muted-foreground">
            Full-stack software engineer delivering high-throughput web
            architectures, clean domain services, and measurable business
            performance.
          </p>
        </motion.div>

        {/* Apple VisionOS Metric Tiles */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-8 sm:mb-10 grid grid-cols-2 gap-3.5 sm:gap-4 md:grid-cols-4"
        >
          {displayStats.map((stat, index) => (
            <motion.div key={index} variants={itemVariants}>
              <LiquidGlassCard
                className="p-5 sm:p-6 text-center rounded-3xl"
                enableTilt
              >
                <div className="inline-flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-primary/15 text-primary mb-3 shadow-[0_0_15px_rgba(201,168,76,0.2),inset_0_1px_1px_rgba(255,255,255,0.2)] group-hover:scale-110 transition-transform duration-300">
                  <stat.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <p className="accent-gradient-text text-xl font-bold sm:text-3xl md:text-4xl tracking-tight">
                  {stat.value}
                </p>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1.5 font-medium leading-tight">
                  {stat.label}
                </p>
              </LiquidGlassCard>
            </motion.div>
          ))}
        </motion.div>

        {/* Main Bio & Details Container */}
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
        >
          <div className="liquid-glass rounded-3xl p-4 sm:p-7 md:p-10 relative overflow-hidden">
            {/* Top Specular Line */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />

            {/* Career Summary */}
            <div className="relative mb-6 sm:mb-10">
              {/* Engineering Profile Badge */}
              <div className="mb-3.5 sm:mb-4 inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]">
                <Sparkles className="h-3 w-3" />
                <span>Engineering Profile & Experience</span>
              </div>

              {aboutData?.bioParagraphs &&
              aboutData.bioParagraphs.length > 0 ? (
                <div className="space-y-3.5 sm:space-y-4">
                  {aboutData.bioParagraphs.map((para, i) => (
                    <p
                      key={i}
                      className={
                        i === 0
                          ? "text-base sm:text-lg lg:text-xl font-medium leading-relaxed text-foreground"
                          : "text-sm sm:text-base leading-relaxed text-muted-foreground"
                      }
                    >
                      {para}
                    </p>
                  ))}
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-3.5">
                  <p className="text-base sm:text-lg lg:text-xl font-medium leading-relaxed text-foreground">
                    Full-Stack{" "}
                    <span className="accent-gradient-text font-semibold">
                      Software Engineer
                    </span>{" "}
                    with{" "}
                    <span className="text-primary font-semibold">
                      4+ years of experience
                    </span>{" "}
                    architecting scalable, high-throughput digital systems.
                  </p>
                  <p className="text-sm sm:text-base leading-relaxed text-muted-foreground">
                    Specialized in modern TypeScript ecosystems across{" "}
                    <span className="text-foreground font-medium">
                      React, Next.js, Node.js, Nest.js, and PostgreSQL
                    </span>
                    . Proven track record of optimizing critical data
                    layers—improving query performance by{" "}
                    <span className="text-primary font-semibold">35%</span> and
                    speeding up frontend rendering by{" "}
                    <span className="text-primary font-semibold">30%</span>{" "}
                    through modular component architecture.
                  </p>
                  <p className="text-sm sm:text-base leading-relaxed text-muted-foreground">
                    Committed to writing clean, maintainable, and type-safe
                    code, standardizing automated testing, and configuring
                    resilient CI/CD pipelines to ensure production reliability.
                  </p>
                </div>
              )}
            </div>

            {/* Subtle Specular Separator */}
            <div className="relative my-6 sm:my-10">
              <div className="h-px w-full bg-gradient-to-r from-transparent via-white/15 to-transparent" />
              <div className="absolute left-1/2 -translate-x-1/2 -top-1 h-2 w-2 rotate-45 border border-primary/40 bg-[#16110a] shadow-[0_0_8px_rgba(201,168,76,0.4)]" />
            </div>

            {/* Split Columns: Education & Training */}
            <div className="grid lg:grid-cols-2 gap-6 sm:gap-8">
              {/* Education */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="flex flex-col min-w-0"
              >
                <div className="flex items-center justify-between gap-2 mb-3.5 sm:mb-4">
                  <div className="flex items-center gap-2 sm:gap-2.5 min-w-0">
                    <div className="flex h-7 w-7 sm:h-8 sm:w-8 shrink-0 items-center justify-center rounded-lg sm:rounded-xl bg-primary/15 border border-primary/25 shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]">
                      <GraduationCap className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
                    </div>
                    <h3 className="text-sm sm:text-base md:text-lg font-bold text-foreground tracking-tight truncate">
                      <span className="hidden sm:inline">Academic </span>
                      Background
                    </h3>
                  </div>
                  <span className="shrink-0 text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider px-2 sm:px-2.5 py-0.5 rounded-full bg-white/[0.04] border border-white/10 text-muted-foreground">
                    Degree
                  </span>
                </div>

                <div className="space-y-3 sm:space-y-3.5 flex-1">
                  {displayEducation.map((item, index) => (
                    <LiquidGlassCard
                      key={item.degree + index}
                      className="p-3.5 sm:p-5 rounded-2xl bg-white/[0.03] border-white/10 hover:border-primary/30 transition-all duration-300"
                    >
                      <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                        <div className="shrink-0 w-9 h-9 sm:w-11 sm:h-11 rounded-xl sm:rounded-2xl bg-primary/15 border border-primary/25 flex items-center justify-center shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)] mt-0.5 sm:mt-0">
                          <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                        </div>
                        <div className="space-y-1 sm:space-y-1.5 min-w-0 flex-1">
                          <h4 className="font-bold text-foreground text-xs sm:text-sm md:text-base leading-snug break-words">
                            {item.degree}
                          </h4>
                          <p className="text-primary font-semibold text-xs sm:text-sm">
                            {item.institute}
                          </p>
                          <div className="flex flex-wrap gap-1.5 sm:gap-2 pt-0.5">
                            <span className="inline-flex items-center gap-1 sm:gap-1.5 px-2 py-0.5 rounded-md bg-white/[0.04] border border-white/10 text-[10px] sm:text-[11px] font-medium text-muted-foreground shrink-0">
                              <Calendar className="w-3 h-3 text-primary/80" />
                              {item.period}
                            </span>
                            <span className="inline-flex items-center gap-1 sm:gap-1.5 px-2 py-0.5 rounded-md bg-white/[0.04] border border-white/10 text-[10px] sm:text-[11px] font-medium text-muted-foreground shrink-0">
                              <MapPin className="w-3 h-3 text-primary/80" />
                              {item.location}
                            </span>
                          </div>
                        </div>
                      </div>
                    </LiquidGlassCard>
                  ))}
                </div>
              </motion.div>

              {/* Professional Training */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="flex flex-col min-w-0"
              >
                <div className="flex items-center justify-between gap-2 mb-3.5 sm:mb-4">
                  <div className="flex items-center gap-2 sm:gap-2.5 min-w-0">
                    <div className="flex h-7 w-7 sm:h-8 sm:w-8 shrink-0 items-center justify-center rounded-lg sm:rounded-xl bg-primary/15 border border-primary/25 shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]">
                      <Award className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
                    </div>
                    <h3 className="text-sm sm:text-base md:text-lg font-bold text-foreground tracking-tight truncate">
                      <span className="hidden sm:inline">Professional </span>
                      Certifications
                    </h3>
                  </div>
                  <span className="shrink-0 text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider px-2 sm:px-2.5 py-0.5 rounded-full bg-primary/10 border border-primary/25 text-primary">
                    {displayCertificates.length} Verified
                  </span>
                </div>

                <div className="space-y-3 sm:space-y-3.5 flex-1">
                  {displayCertificates.map((item) => (
                    <LiquidGlassCard
                      key={item.id}
                      className="p-3.5 sm:p-4.5 rounded-2xl bg-white/[0.03] border-white/10 hover:border-primary/30 transition-all duration-300"
                    >
                      <div className="flex items-center justify-between gap-2.5 sm:gap-3.5 min-w-0">
                        <div className="flex items-center gap-2.5 sm:gap-3.5 min-w-0 flex-1">
                          <div className="shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-primary/15 border border-primary/25 flex items-center justify-center shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]">
                            <Award className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="font-bold text-xs sm:text-sm text-foreground leading-snug truncate">
                              {item.title}
                            </h4>
                            <div className="flex items-center gap-1.5 sm:gap-2 mt-0.5 sm:mt-1 text-[11px] sm:text-xs">
                              <span className="text-primary font-medium truncate max-w-[105px] sm:max-w-none">
                                {item.provider}
                              </span>
                              <span className="text-muted-foreground/60">
                                •
                              </span>
                              <span className="text-muted-foreground shrink-0">
                                {item.year}
                              </span>
                            </div>
                          </div>
                        </div>

                        <button
                          type="button"
                          className="shrink-0 group/btn inline-flex items-center gap-1 sm:gap-1.5 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full bg-primary/10 hover:bg-primary/20 border border-primary/25 text-[11px] sm:text-xs font-semibold text-primary transition-all duration-200 hover:scale-105 active:scale-95 shadow-[0_0_10px_rgba(201,168,76,0.1)]"
                          onClick={() => setActiveCertificateId(item.id)}
                          aria-label={`View ${item.title} certificate`}
                        >
                          <span>View</span>
                          <Eye className="h-3 w-3 transition-transform group-hover/btn:scale-110" />
                        </button>
                      </div>
                    </LiquidGlassCard>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* VisionOS Frosted Glass Certificate Modal */}
      {activeCertificate &&
        isMounted &&
        createPortal(
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
            <button
              type="button"
              aria-label="Close certificate modal"
              className="absolute inset-0 bg-black/80 backdrop-blur-md transition-opacity"
              onClick={() => setActiveCertificateId(null)}
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              data-lenis-prevent="true"
              className="relative z-10 flex flex-col w-[min(210mm,calc(100vw-2rem))] max-h-[calc(100svh-2rem)] overflow-hidden rounded-3xl border border-white/20 bg-[#14100b]/95 backdrop-blur-3xl shadow-[0_30px_90px_rgba(0,0,0,0.95),inset_0_1px_1.5px_rgba(255,255,255,0.3)]"
            >
              <div className="shrink-0 flex items-center justify-between border-b border-white/10 px-5 py-3.5 sm:px-6 bg-[#14100b]/90 backdrop-blur-md">
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    Certificate Preview
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {activeCertificate.title} — {activeCertificate.provider} (
                    {activeCertificate.year})
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    asChild
                    size="sm"
                    className="liquid-pill-btn h-9 rounded-full px-4 text-xs font-semibold text-black"
                  >
                    <a
                      href={getPdfDownloadUrl(
                        activeCertificate.downloadPath,
                        activeCertificate.downloadName,
                      )}
                      download={activeCertificate.downloadName}
                      className="flex items-center gap-1.5"
                    >
                      <span>Download</span>
                      <Download className="h-3.5 w-3.5" />
                    </a>
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Close certificate preview"
                    className="h-10 w-10 sm:h-9 sm:w-9 rounded-full border border-white/10 bg-white/[0.05] hover:bg-white/[0.1] active:bg-white/[0.2] active:scale-95 text-foreground shrink-0 cursor-pointer"
                    onClick={() => setActiveCertificateId(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="flex-1 min-h-0 overflow-hidden relative">
                <PdfPreview
                  downloadPath={getPdfPreviewUrl(
                    activeCertificate.downloadPath,
                  )}
                  title={`${activeCertificate.title} Certificate Preview`}
                />
              </div>
            </motion.div>
          </div>,
          document.body,
        )}
    </section>
  );
}
